// Impersonation runs in a *separate browser tab* so the admin's own session
// stays alive in the original window. Browsers can't open a private/incognito
// window from script and tabs of the same origin share `localStorage`, so the
// impersonation tab keeps its auth tokens in tab-scoped `sessionStorage`
// instead. A flag (also in sessionStorage) marks the tab as impersonating; the
// auth layer routes all token reads/writes through `authStorage()` accordingly.
//
// Hand-off: the admin tab calls /auth/impersonate (authorized by *its* token),
// drops the resulting auth payload under a one-time key in the shared
// localStorage, then opens a new tab at `/home?impersonate_handoff=<id>`.
// That tab consumes the payload, removes the key, sets the flag and adopts the
// session into sessionStorage — never touching the admin's localStorage tokens.

import type { UserRole } from 'api/vektra/types';

export const IMPERSONATION_FLAG = 'vektra_impersonation';
export const IMPERSONATION_HANDOFF_PREFIX = 'vektra_impersonation_handoff_';
export const IMPERSONATION_HANDOFF_PARAM = 'impersonate_handoff';

/** Roles allowed to impersonate (mirrors the backend @PreAuthorize on /auth/impersonate). */
const IMPERSONATOR_ROLES: UserRole[] = ['SUPER_ADMIN', 'ADMIN', 'ACCOUNT_MANAGER'];

/** Whether any of the given roles may impersonate other users. */
export function canImpersonate(roles: string[] | undefined): boolean {
  return !!roles && roles.some((role) => IMPERSONATOR_ROLES.includes(role as UserRole));
}

/** App-level admin roles that can never be impersonated (mirrors the backend guard). */
const NON_IMPERSONATABLE_ROLES: UserRole[] = ['SUPER_ADMIN', 'ADMIN'];

/** Whether a user with the given role is allowed to be impersonated. */
export function canBeImpersonated(role: string | undefined): boolean {
  return !!role && !NON_IMPERSONATABLE_ROLES.includes(role as UserRole);
}

/** True when the current tab is an impersonation tab. */
export function isImpersonationSession(): boolean {
  try {
    return sessionStorage.getItem(IMPERSONATION_FLAG) === 'true';
  } catch {
    return false;
  }
}

/**
 * The storage backing the auth tokens for the current tab: tab-scoped
 * sessionStorage while impersonating, shared localStorage otherwise.
 */
export function authStorage(): Storage {
  return isImpersonationSession() ? sessionStorage : localStorage;
}
